import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Stack, Step, StepLabel, Stepper } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { colors } from "styles/colors";
import useGlobalStyles from "styles/globalStyles";
import CustomText from "components/common/CustomText/CustomText";
import { RegistryDataType } from "types/types";
import CreateRegistryStepOne from "./CreateRegistryFormStepOne";
import CreateRegistryStepTwo from "./CreateRegistryFormStepTwo";
import FolderIcon from '@mui/icons-material/Folder';
import CustomButton from "components/buttons/CustomButton/CustomButton";
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { useCreateRegistry, useGetRegistryById, useUpdateRegistry } from "hooks/useRegistries";
import { useAuth } from "context/AuthContext";
import * as yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import { snackbarService } from "services/snackbarService";
import { useRegistryContext } from "context/RegistryContext";


const validationSchema: yup.ObjectSchema<RegistryDataType> = yup.object({
  name: yup.string().required('Registry name is required'),
  description: yup.string().required('Description is required'),
  users: yup.array().default([]),
  fields: yup.array().optional(),
  user_id: yup.number().optional(),
});



const steps = ["Registry Details", "Customized Fields"];

export default function CreateRegistryForm() {
  const { user } = useAuth();
  const globalStyles = useGlobalStyles();
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const registryId = Number(useParams().registryId);
  const { refetch } = useRegistryContext();

  const initialDefaultValues = {
    name: '',
    description: '',
    users: [],
  };

  const methods = useForm<RegistryDataType>({
    resolver: yupResolver(validationSchema),
    shouldUnregister: false,
    mode: "onChange",
    defaultValues: initialDefaultValues,
  });

  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isValid },
  } = methods;

  useGetRegistryById(registryId, {
    enabled: !!registryId,
    onSuccess: (res: any) => {
      const { name, description } = res.data;
      reset({ name, description });
    },
    onError: (err: any) => {
      console.error('error', err);
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!registryId) {
      reset({ name: '', description: '', users: [] })
    }
  }, [registryId])

  const { mutate: createMutation, isLoading: isCreating } = useCreateRegistry(
    (res: any) => {
      const { data, status_code: statusCode } = res;
      if (statusCode == 200) {
        reset(initialDefaultValues);
        navigate(`/registries/all-registries/${res.data.id}`);
      } else {
        snackbarService.showSnackbar(res?.data ?? 'Something went wrong, please try again', 'error');
      }
    },
    (err: any) => console.log('create reg err', err),
  )

  const { mutate: updateMutation, isLoading: isUpdating } = useUpdateRegistry(
    (res: any) => {
      const { data, status_code: statusCode } = res;
      console.log('res update', res);
      if (statusCode == 200) {
        navigate(-1);
        refetch();
        snackbarService.showSnackbar('Registry has been updated successfully', 'success');
      } else {
        snackbarService.showSnackbar(res?.data ?? 'Something went wrong, please try again', 'error');
      }
    },
    (err: any) => console.log('create reg err', err),
  )

  const handleUpdate = () => {
    console.log('hello values', getValues())
    updateMutation({
      registryId,
      ...getValues(),
    })
  }

  const onSubmit = (data: RegistryDataType) => {
    createMutation({
      user_id: user.id,
      ...data,
    });
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    setActiveStep((prev) => prev + 1);
  };
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <CreateRegistryStepOne />;
      case 1:
        return <CreateRegistryStepTwo />;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        className={globalStyles.sectionContainer}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden"
        }}
      >
        <Box className={globalStyles.sectionHeader}>
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            {activeStep == 0 ? <FolderIcon sx={{ fontSize: 24, color: colors.primary }} /> : <TextFieldsIcon sx={{ fontSize: 24, color: colors.primary }} />}
            <Box>
              <CustomText
                text={steps[activeStep]}
                color={colors.primary}
                fontWeight={600}
              />
              {activeStep == 1 && <CustomText
                text="Customize case fields to suit the registry topic."
                fontSize={12}
                color={colors.secondary}
              />}
            </Box>
          </Stack>
        </Box>

        <Divider />

        {renderStep()}

        <Stack mx={3} my={2} direction={'row'} justifyContent={'space-between'}>
          <CustomButton
            text='Back'
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0 || isCreating}
            minWidth={100}
          />

          {registryId ? (
            <CustomButton
              text='Save'
              onClick={handleUpdate}
              disabled={!isValid || isCreating || isUpdating}
              loading={isCreating || isUpdating}
              minWidth={100}
            />
          ) : activeStep == 0 ? (<CustomButton
            text='Next'
            onClick={handleNext}
            disabled={!isValid || isCreating}
            minWidth={100}
          />) : (
            <CustomButton
              text="Create registry"
              type="submit"
              disabled={!isValid || isCreating}
              loading={isCreating}
            />
          )}

        </Stack>
      </Box>

    </FormProvider>
  );
}
